// src/redux/companyAdhoc/companyAdhocSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createCompanyAdhocAPI,
  getCompanyAdhocListingAPI,
  getCompanyAdhocToolsCandidatesCountAPI,
  getCompanyAdhocToolsAPI,
  getCompanyAdhocDetailAPI,
  editCompanyAdhocAPI,
  archiveCompanyAdhocAPI,
} from "./Companyadhocapi";

// ✅ Create Company Adhoc
export const createCompanyAdhoc = createAsyncThunk(
  "companyAdhoc/createCompanyAdhoc",
  async (adhocData, { rejectWithValue }) => {
    try {
      const response = await createCompanyAdhocAPI(adhocData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create company adhoc");
    }
  }
);

// ✅ Get Company Adhoc Listing
export const getCompanyAdhocListing = createAsyncThunk(
  "companyAdhoc/getCompanyAdhocListing",
  async ({ page = 1, limit = 10, search = "", is_archived = "false" } = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanyAdhocListingAPI({ page, limit, search, is_archived });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company adhoc listing");
    }
  }
);

// ✅ Get Company Adhoc Tools Candidates Count
export const getCompanyAdhocToolsCandidatesCount = createAsyncThunk(
  "companyAdhoc/getCompanyAdhocToolsCandidatesCount",
  async ({ page = 1, limit = 10, search = "", company_id } = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanyAdhocToolsCandidatesCountAPI({ page, limit, search, company_id });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch tools candidates count");
    }
  }
);

// ✅ Get Company Adhoc Tools
export const getCompanyAdhocTools = createAsyncThunk(
  "companyAdhoc/getCompanyAdhocTools",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyAdhocToolsAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company adhoc tools");
    }
  }
);

// ✅ Get Company Adhoc Detail
export const getCompanyAdhocDetail = createAsyncThunk(
  "companyAdhoc/getCompanyAdhocDetail",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyAdhocDetailAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company adhoc detail");
    }
  }
);

// ✅ Edit Company Adhoc
export const editCompanyAdhoc = createAsyncThunk(
  "companyAdhoc/editCompanyAdhoc",
  async ({ companyId, adhocData }, { rejectWithValue }) => {
    try {
      const response = await editCompanyAdhocAPI(companyId, adhocData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to edit company adhoc");
    }
  }
);

// ✅ Archive Company Adhoc
export const archiveCompanyAdhoc = createAsyncThunk(
  "companyAdhoc/archiveCompanyAdhoc",
  async ({ companyId, isArchive = true }, { rejectWithValue }) => {
    try {
      const response = await archiveCompanyAdhocAPI(companyId, isArchive);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive company adhoc");
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  createCompanyAdhoc: { data: null, loading: false, error: null },
  getCompanyAdhocListing: { data: [], total: 0, loading: false, error: null },
  getCompanyAdhocToolsCandidatesCount: { data: null, loading: false, error: null },
  getCompanyAdhocTools: { data: null, loading: false, error: null },
  getCompanyAdhocDetail: { data: null, loading: false, error: null },
  editCompanyAdhoc: { data: null, loading: false, error: null },
  archiveCompanyAdhoc: { data: null, loading: false, error: null },
};

// ---------- Slice ----------
const companyAdhocSlice = createSlice({
  name: "companyAdhoc",
  initialState,
  reducers: {
    clearCreateCompanyAdhoc: (state) => {
      state.createCompanyAdhoc.data = null;
      state.createCompanyAdhoc.error = null;
    },
    clearCompanyAdhocListing: (state) => {
      state.getCompanyAdhocListing.data = [];
      state.getCompanyAdhocListing.error = null;
      state.getCompanyAdhocListing.total = 0;
    },
    clearCompanyAdhocToolsCandidatesCount: (state) => {
      state.getCompanyAdhocToolsCandidatesCount.data = null;
      state.getCompanyAdhocToolsCandidatesCount.error = null;
    },
    clearCompanyAdhocTools: (state) => {
      state.getCompanyAdhocTools.data = null;
      state.getCompanyAdhocTools.error = null;
    },
    clearCompanyAdhocDetail: (state) => {
      state.getCompanyAdhocDetail.data = null;
      state.getCompanyAdhocDetail.error = null;
    },
    clearEditCompanyAdhoc: (state) => {
      state.editCompanyAdhoc.data = null;
      state.editCompanyAdhoc.error = null;
    },
    clearArchiveCompanyAdhoc: (state) => {
      state.archiveCompanyAdhoc.data = null;
      state.archiveCompanyAdhoc.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---------- Create Company Adhoc ----------
    builder
      .addCase(createCompanyAdhoc.pending, (state) => {
        state.createCompanyAdhoc.loading = true;
        state.createCompanyAdhoc.error = null;
      })
      .addCase(createCompanyAdhoc.fulfilled, (state, action) => {
        state.createCompanyAdhoc.loading = false;
        state.createCompanyAdhoc.data = action.payload?.data || action.payload;
      })
      .addCase(createCompanyAdhoc.rejected, (state, action) => {
        state.createCompanyAdhoc.loading = false;
        state.createCompanyAdhoc.error = action.payload;
      });

    // ---------- Get Company Adhoc Listing ----------
    builder
      .addCase(getCompanyAdhocListing.pending, (state) => {
        state.getCompanyAdhocListing.loading = true;
        state.getCompanyAdhocListing.error = null;
      })
      .addCase(getCompanyAdhocListing.fulfilled, (state, action) => {
        state.getCompanyAdhocListing.loading = false;
        state.getCompanyAdhocListing.data = action.payload?.data || [];
        state.getCompanyAdhocListing.total = action.payload?.total || 0;
      })
      .addCase(getCompanyAdhocListing.rejected, (state, action) => {
        state.getCompanyAdhocListing.loading = false;
        state.getCompanyAdhocListing.error = action.payload;
      });

    // ---------- Get Company Adhoc Tools Candidates Count ----------
    builder
      .addCase(getCompanyAdhocToolsCandidatesCount.pending, (state) => {
        state.getCompanyAdhocToolsCandidatesCount.loading = true;
        state.getCompanyAdhocToolsCandidatesCount.error = null;
      })
      .addCase(getCompanyAdhocToolsCandidatesCount.fulfilled, (state, action) => {
        state.getCompanyAdhocToolsCandidatesCount.loading = false;
        state.getCompanyAdhocToolsCandidatesCount.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyAdhocToolsCandidatesCount.rejected, (state, action) => {
        state.getCompanyAdhocToolsCandidatesCount.loading = false;
        state.getCompanyAdhocToolsCandidatesCount.error = action.payload;
      });

    // ---------- Get Company Adhoc Tools ----------
    builder
      .addCase(getCompanyAdhocTools.pending, (state) => {
        state.getCompanyAdhocTools.loading = true;
        state.getCompanyAdhocTools.error = null;
      })
      .addCase(getCompanyAdhocTools.fulfilled, (state, action) => {
        state.getCompanyAdhocTools.loading = false;
        state.getCompanyAdhocTools.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyAdhocTools.rejected, (state, action) => {
        state.getCompanyAdhocTools.loading = false;
        state.getCompanyAdhocTools.error = action.payload;
      });

    // ---------- Get Company Adhoc Detail ----------
    builder
      .addCase(getCompanyAdhocDetail.pending, (state) => {
        state.getCompanyAdhocDetail.loading = true;
        state.getCompanyAdhocDetail.error = null;
      })
      .addCase(getCompanyAdhocDetail.fulfilled, (state, action) => {
        state.getCompanyAdhocDetail.loading = false;
        state.getCompanyAdhocDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyAdhocDetail.rejected, (state, action) => {
        state.getCompanyAdhocDetail.loading = false;
        state.getCompanyAdhocDetail.error = action.payload;
      });

    // ---------- Edit Company Adhoc ----------
    builder
      .addCase(editCompanyAdhoc.pending, (state) => {
        state.editCompanyAdhoc.loading = true;
        state.editCompanyAdhoc.error = null;
      })
      .addCase(editCompanyAdhoc.fulfilled, (state, action) => {
        state.editCompanyAdhoc.loading = false;
        state.editCompanyAdhoc.data = action.payload?.data || action.payload;
      })
      .addCase(editCompanyAdhoc.rejected, (state, action) => {
        state.editCompanyAdhoc.loading = false;
        state.editCompanyAdhoc.error = action.payload;
      });

    // ---------- Archive Company Adhoc ----------
    builder
      .addCase(archiveCompanyAdhoc.pending, (state) => {
        state.archiveCompanyAdhoc.loading = true;
        state.archiveCompanyAdhoc.error = null;
      })
      .addCase(archiveCompanyAdhoc.fulfilled, (state, action) => {
        state.archiveCompanyAdhoc.loading = false;
        state.archiveCompanyAdhoc.data = action.payload?.data || action.payload;
      })
      .addCase(archiveCompanyAdhoc.rejected, (state, action) => {
        state.archiveCompanyAdhoc.loading = false;
        state.archiveCompanyAdhoc.error = action.payload;
      });
  },
});

export const {
  clearCreateCompanyAdhoc,
  clearCompanyAdhocListing,
  clearCompanyAdhocToolsCandidatesCount,
  clearCompanyAdhocTools,
  clearCompanyAdhocDetail,
  clearEditCompanyAdhoc,
  clearArchiveCompanyAdhoc,
} = companyAdhocSlice.actions;

export default companyAdhocSlice.reducer;